import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "scheduleData", 
    "frequencyValue", "frequencyType",
    "dayButton", "weekDaySelector", "monthDaySelector", "monthSelector", "monthDay", "month", "yearMonthDay",
    "timeInput", "timeContainer",
    "endTypeNever", "endTypeDate", "endTypeOccurrences",
    "endDateContainer", "endOccurrencesContainer",
    "endDate", "endOccurrences",
    "duedateDay", "duedateDayInput", "weekendAllowed", "weekendAllowedHidden"
  ]
  
  connect() {
    this.initializeSchedule()
    
    // Ha van weekendAllowed target, add event listener a Stimulus events használatával
    if (this.hasWeekendAllowedTarget) {
      this.weekendAllowedTarget.addEventListener('change', this.updateWeekendAllowedHidden.bind(this));
    }
    
    // Find the form and add submit listener
    const form = this.element.closest('form');
    if (form) {
      form.addEventListener('submit', this.onFormSubmit.bind(this));
    }
  }
  
  // Handle form submission
  onFormSubmit() {
    if (this.hasWeekendAllowedTarget && this.hasWeekendAllowedHiddenTarget) {
      const isChecked = this.weekendAllowedTarget.checked;
      this.weekendAllowedHiddenTarget.value = isChecked ? '1' : '0';
      console.log('Form submit: weekend_allowed set to', isChecked, 'hidden field:', this.weekendAllowedHiddenTarget.value);
    }
  }
  
  // Update the hidden field when the checkbox changes
  updateWeekendAllowedHidden(event) {
    if (this.hasWeekendAllowedHiddenTarget) {
      this.weekendAllowedHiddenTarget.value = event.target.checked ? '1' : '0';
      console.log('Checkbox changed to', event.target.checked, 'hidden field updated to', this.weekendAllowedHiddenTarget.value);
    }
  }
  
  // Handle day button click
  toggleDaySelection(event) {
    event.currentTarget.classList.toggle('active')
    this.updateSchedule()
  }

  // Update schedule on time change
  handleTimeChange() {
    this.updateSchedule()
  }

  // Handle frequency type change
  onFrequencyTypeChange() {
    const frequencyType = this.frequencyTypeTarget.value
    
    // Show/hide appropriate selectors
    // Weekly frequency - show day buttons
    this.weekDaySelectorTarget.classList.toggle('d-none', frequencyType !== 'week')
    
    // Monthly frequency - show day selector
    this.monthDaySelectorTarget.classList.toggle('d-none', frequencyType !== 'month')
    
    // Yearly frequency - show month and day selector
    if (this.hasMonthSelectorTarget) {
      this.monthSelectorTarget.classList.toggle('d-none', frequencyType !== 'year')
    }
    
    this.updateSchedule()
  }

  // Update end type sections visibility
  onEndTypeChange() {
    this.updateEndType()
  }

  // Update end type sections visibility
  updateEndType() {
    // Hide all containers first
    if (this.hasEndDateContainerTarget) {
      this.endDateContainerTarget.classList.add('d-none')
    }
    
    if (this.hasEndOccurrencesContainerTarget) {
      this.endOccurrencesContainerTarget.classList.add('d-none')
    }
    
    // Show the selected container
    if (this.hasEndTypeDateTarget && this.endTypeDateTarget.checked) {
      if (this.hasEndDateContainerTarget) {
        this.endDateContainerTarget.classList.remove('d-none')
      }
    } else if (this.hasEndTypeOccurrencesTarget && this.endTypeOccurrencesTarget.checked) {
      if (this.hasEndOccurrencesContainerTarget) {
        this.endOccurrencesContainerTarget.classList.remove('d-none')
      }
    }
    
    this.updateSchedule()
  }

  // Update schedule data from form inputs
  updateSchedule() {
    if (!this.hasScheduleDataTarget) return
    
    const scheduleData = {}
    
    // Set frequency data
    if (this.hasFrequencyValueTarget && this.hasFrequencyTypeTarget) {
      scheduleData.frequency = {
        value: parseInt(this.frequencyValueTarget.value) || 1,
        type: this.frequencyTypeTarget.value
      }
    } else {
      scheduleData.frequency = { value: 1, type: 'week' }
    }

    // Get time value
    const timeValue = this.hasTimeInputTarget ? this.timeInputTarget.value : "09:00"
    
    // Handle different frequency types
    if (scheduleData.frequency.type === 'week' && this.hasDayButtonTarget) {
      // Weekly frequency
      scheduleData.days = {}
      this.dayButtonTargets.forEach(button => {
        if (button.classList.contains('active')) {
          scheduleData.days[button.dataset.day] = [timeValue]
        }
      })
    } else if (scheduleData.frequency.type === 'month' && this.hasMonthDayTarget) {
      // Monthly frequency
      scheduleData.monthDay = parseInt(this.monthDayTarget.value) || 1
      scheduleData.times = [timeValue]
    } else if (scheduleData.frequency.type === 'year') {
      // Yearly frequency
      
      // Get day value from yearMonthDay target or fallback to element with year-month-day class
      const dayValue = this.hasYearMonthDayTarget 
        ? parseInt(this.yearMonthDayTarget.value) || 1 
        : parseInt(this.element.querySelector('.year-month-day')?.value) || 1
        
      scheduleData.monthDay = dayValue
      scheduleData.times = [timeValue]
      
      // Set month for yearly schedule (1-12)
      if (this.hasMonthTarget) {
        // Ensure month is always a number (1-12)
        const monthValue = parseInt(this.monthTarget.value) || 1
        scheduleData.month = monthValue
        console.log(`Yearly schedule: Setting month value to ${monthValue}`)
      } else {
        scheduleData.month = 1
      }
    } else {
      // Daily frequency
      scheduleData.times = [timeValue]
    }
    
    // Set end type settings
    if (this.hasEndTypeNeverTarget && this.hasEndTypeDateTarget && this.hasEndTypeOccurrencesTarget) {
      if (this.endTypeNeverTarget.checked) {
        scheduleData.endType = 'never'
      } else if (this.endTypeDateTarget.checked) {
        scheduleData.endType = 'on_date'
        if (this.hasEndDateTarget) {
          scheduleData.endDate = this.endDateTarget.value
        }
      } else if (this.endTypeOccurrencesTarget.checked) {
        scheduleData.endType = 'after_occurrences'
        if (this.hasEndOccurrencesTarget) {
          scheduleData.endOccurrences = parseInt(this.endOccurrencesTarget.value) || 10
        }
      }
    } else {
      scheduleData.endType = 'never'
    }
    
    // Add due date day and weekend allowance
    if (this.hasDuedateDayInputTarget) {
      scheduleData.duedateDay = parseInt(this.duedateDayInputTarget.value) || 0
    }
    
    // Get the weekend allowed value from the UI checkbox
    if (this.hasWeekendAllowedTarget) {
      // Explicitly read from the checkbox control in the UI
      const isChecked = this.weekendAllowedTarget.checked;
      scheduleData.weekendAllowed = isChecked;
      console.log('Read weekend_allowed from UI checkbox:', isChecked);
    }
    
    // Update hidden field for JSON data
    this.scheduleDataTarget.value = JSON.stringify(scheduleData)
    
    // Update the separate duedate_day hidden field
    if (this.hasDuedateDayTarget) {
      this.duedateDayTarget.value = scheduleData.duedateDay || 0
    }
    
    // Explicitly set the hidden field for weekend_allowed using target
    if (this.hasWeekendAllowedHiddenTarget) {
      // Use 1/0 for better Rails compatibility
      this.weekendAllowedHiddenTarget.value = scheduleData.weekendAllowed ? '1' : '0'
      console.log(`Setting weekend_allowed hidden field to ${scheduleData.weekendAllowed}: ${this.weekendAllowedHiddenTarget.value}`);
    }
  }

  // Initialize from existing schedule data
  initializeSchedule() {
    if (!this.hasScheduleDataTarget) return
    
    try {
      console.log("Initializing schedule from:", this.scheduleDataTarget.value)
      
      // Use default if data is empty or invalid
      if (!this.scheduleDataTarget.value || this.scheduleDataTarget.value === "null" || this.scheduleDataTarget.value === "{}") {
        this.setDefaultSchedule()
        return
      }
      
      // Parse schedule data
      let scheduleData
      try {
        scheduleData = JSON.parse(this.scheduleDataTarget.value)
      } catch (e) {
        console.error("Error parsing schedule data:", e)
        this.setDefaultSchedule()
        return
      }
      
      if (!scheduleData) {
        this.setDefaultSchedule()
        return
      }
      
      console.log("Parsed schedule data:", scheduleData)
      
      // Set form values from data
      this.setFrequency(scheduleData)
      this.setWeekDays(scheduleData)
      this.setMonthDay(scheduleData)
      this.setMonth(scheduleData)
      this.setTime(scheduleData)
      this.setEndType(scheduleData)
      this.setDuedateDay(scheduleData)
      this.setWeekendAllowed(scheduleData)
      
      // Trigger frequency type change to show/hide appropriate sections
      this.onFrequencyTypeChange()
      
    } catch (error) {
      console.error("Error initializing schedule:", error)
      this.setDefaultSchedule()
    }
  }
  
  // Set frequency values
  setFrequency(data) {
    if (data.frequency && this.hasFrequencyValueTarget && this.hasFrequencyTypeTarget) {
      this.frequencyValueTarget.value = data.frequency.value || 1
      this.frequencyTypeTarget.value = data.frequency.type || 'week'
      
      // Update visibility of sections based on frequency type
      this.onFrequencyTypeChange()
    }
  }
  
  // Set weekly days selection
  setWeekDays(data) {
    if (data.days && this.hasDayButtonTarget) {
      this.dayButtonTargets.forEach(button => {
        if (data.days[button.dataset.day]) {
          button.classList.add('active')
        }
      })
    }
  }
  
  // Set monthly day
  setMonthDay(data) {
    if (data.monthDay) {
      // Havi nézet napjának beállítása
      if (this.hasMonthDayTarget) {
        this.monthDayTarget.value = data.monthDay
      }
      
      // Éves nézet napjának beállítása
      if (this.hasYearMonthDayTarget) {
        this.yearMonthDayTarget.value = data.monthDay
      } else {
      const yearMonthDaySelect = this.element.querySelector('.year-month-day')
      if (yearMonthDaySelect) {
        yearMonthDaySelect.value = data.monthDay
        }
      }
    }
  }
  
  // Set month for yearly schedule
  setMonth(data) {
    if (data.month && this.hasMonthTarget) {
      // Konvertáljuk számra, majd vissza stringre, hogy biztosan helyes legyen
      const monthValue = parseInt(data.month) || 1
      this.monthTarget.value = monthValue.toString()
      
      console.log(`Setting month value to: ${monthValue}`)
    }
  }
  
  // Set time input
  setTime(data) {
    if (this.hasTimeInputTarget) {
      let time = "09:00"; 
      
      if (data.frequency && data.frequency.type === 'week' && data.days) {
        // Get time from weekly schedule
        for (const day in data.days) {
          if (data.days[day] && Array.isArray(data.days[day]) && data.days[day].length > 0) {
            time = data.days[day][0]
            break
          }
        }
      } else if (data.times && data.times.length > 0) {
        // Get time from times array
        time = data.times[0]
      }
      
      this.timeInputTarget.value = time
    }
  }
  
  // Set end type and values
  setEndType(data) {
    if (this.hasEndTypeDateTarget && this.hasEndTypeOccurrencesTarget && this.hasEndTypeNeverTarget) {
      if (data.endType === 'on_date' && data.endDate && this.hasEndDateTarget) {
        this.endTypeDateTarget.checked = true
        this.endDateTarget.value = data.endDate
      } else if (data.endType === 'after_occurrences' && data.endOccurrences && this.hasEndOccurrencesTarget) {
        this.endTypeOccurrencesTarget.checked = true
        this.endOccurrencesTarget.value = data.endOccurrences
      } else {
        this.endTypeNeverTarget.checked = true
      }
      
      this.updateEndType()
    }
  }

  // Set default schedule values
  setDefaultSchedule() {
    const defaultSchedule = {
      frequency: { value: 1, type: "week" },
      days: { monday: ["09:00"] },
      endType: "never",
      duedateDay: 0,
      weekendAllowed: false
    }
    
    this.scheduleDataTarget.value = JSON.stringify(defaultSchedule)
    
    // Set form fields to defaults
    if (this.hasFrequencyTypeTarget) {
      this.frequencyTypeTarget.value = "week"
      this.onFrequencyTypeChange()
    }
    
    if (this.hasFrequencyValueTarget) {
      this.frequencyValueTarget.value = 1
    }
    
    if (this.hasDayButtonTarget && this.dayButtonTargets.length > 0) {
      // Set Monday active by default
      const mondayButton = this.dayButtonTargets.find(button => button.dataset.day === "monday")
      if (mondayButton) {
        mondayButton.classList.add('active')
      }
    }
    
    if (this.hasTimeInputTarget) {
      this.timeInputTarget.value = "09:00"
    }
    
    if (this.hasEndTypeNeverTarget) {
      this.endTypeNeverTarget.checked = true
      this.updateEndType()
    }
    
    if (this.hasDuedateDayInputTarget) {
      this.duedateDayInputTarget.value = 0
    }
    
    if (this.hasWeekendAllowedTarget) {
      this.weekendAllowedTarget.checked = false
    }
  }
  
  // Set due date day
  setDuedateDay(data) {
    if (this.hasDuedateDayInputTarget) {
      this.duedateDayInputTarget.value = data.duedateDay || 0
    }
  }
  
  // Set weekend allowance
  setWeekendAllowed(data) {
    if (this.hasWeekendAllowedTarget) {
      this.weekendAllowedTarget.checked = data.weekendAllowed || false
    }
  }
} 